// Need to use the React-specific entry point to import createApi
import { BluesnapCartInfo, BluesnapErrorOutput, BluesnapShoppingContext } from "@common-models/bluesnap-cart-info";
import { Campaign, CampaignTiming } from "@common-models/campaign-model";
import { Invoice } from "@common-models/invoice";
import { MessageLog } from "@common-models/message-log";
import { BluesnapInvoice } from "@common-models/order";
import { Subscription } from "@common-models/subscription";
import { User, UserInterface } from "@common-models/user";
import { taskActions } from "@common-reducers/TasksReducer";
import { PlanDescriptor, appConfig } from "@common/config/app.config";
import { LinkPreview, UserMessage } from "@common/models/user-message";
import { Settings } from "@common/types/interfaces";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { SubscriptionResponse } from 'bluesnap';
import qs from "qs";

// import { DBUserMessageThunks } from "@common-reducers/DBServiceThunks";
import { BluesnapShopper } from "@common-models/bluesnap-shopper";
import { ShopperInfo } from "@common-models/shopper-info";
import { setSettings } from "@common-reducers/MainReducer";
import { ArchivedOrDeletedItem } from '@common-reducers/RecycleBinReducer';
import { ConnectedSocket } from '@common-reducers/ConnectedEnginesSlice';
import { CommonRootState } from "@common/types/common-root-state-type";
import { UserState } from "../UserReducer";
import { authenticateOnQueryStarted, waCommRequestOnQueryStarted } from "./backend-api-query-handlers";
import { chargebeeApiBuilder } from "./chargebee-api";
import { recycleApiBuilder } from "./recycle-api";
import { DocumentType } from '@common/types/document-types';
import { EventType } from '@common/models/node-event';



export interface ImportExcelToBoardOptions {
  boardId: string;
  importDataMapping: Record<string, string>;
  selectedColumn: string;
  sheetName: string;
  importFileName: string;
  file: File;
}

export interface UpdateManyRequest<T> {
  cmd: string;
  entities: T[];
  service: string;
}

export type UpdateManyResponse<T> = T[];
export type CreateManyResponse<T> = T[];


export interface Paginated<T> {
  total: number;
  limit: number;
  skip: number;
  data: T[];
}
export interface AuthParams {
  strategy: string;
  email?: string;
  password?: string;
  accessToken?: string;
  impersonate?: string;
}

export interface GenerateUploadUrlResponse {
  uploadUrl: string;
  publicUrl: string;
  mediaId: string;
}

export interface AuthResponse {
  accessToken: string;
  user: UserInterface;
}

export interface FindParams {
  service: string;
  query?: any;
}

export interface GetParams {
  service: string;
  _id: string;
}

export interface CreateParams {
  service: string;
  entity: any;
}

export interface CreateManyParams {
  service: string;
  entities: any;
}

export interface ResetPasswordParams {
  token: string;
  password: string;
}
export interface NextCampaignMessageResponse {
  campaign?: Campaign;
  campaignMessages: UserMessage[];
}

export interface duplicateAudienceBoardForCampaignsResponse {
  newDuplicateBoardId: string;
}

export interface duplicateTemplateForCampaignsResponse {
  newDuplicateTemplateId: string;
}

export interface MessageSentResponse {
  message: UserMessage;
  campaign?: Campaign;
  messageLog: MessageLog;
}

export interface CampaignSendTimeParams {
  sendCount: number;
  timing?: CampaignTiming;
}

export interface UserPaymentParams {
  params: string;
  plan: PlanDescriptor;
  discount: number;
  orignalPrice: number;
  subscription: SubscriptionResponse;
}

export interface GetUserPaymentParamsRequest {
  productId: string;
}

export interface CalculateCartParams {
  planId?: string; // The plan that relates to sku contract (for paypal payments)
  skuId: string;
  currency?: string;
  couponCode?: string;
  currencyConversionRateFromDollar?: number;
}

export interface ShoppingContextParams extends CalculateCartParams {
  firstName: string;
  lastName: string;
  countryCode: string;
  companyName: string;
  address: string;
  address2?: string;
  zip?: string;
  vatId: string;
  workspaceId: string;
  ccToken?: string;
}

export interface GetCheckoutPageUrlParams {
  item_price_id: string;
  quantity: number;
}

export interface GetCheckoutPageUrlResponse {
  checkoutPageUrl: string;
  debug?: {
    function: string;
    input: any;
  };
}

export interface AcceptInventionResponse {
  workspaceId: string;
  joined: boolean;
  authorized: boolean;
  error?: string;
}

export interface AcceptWorkspaceInvitationParams {
  workspaceId: string;
  inviteId: string;
}

export interface AcceptBoardInvitationParams {
  boardId: string;
  inviteId: string;
}

export interface GetItemPricesResponse {
  itemPrices: Array<{
    itemType: string;
    periodUnit: string;
    price: number;
  }>;
}

export interface ProcessDocumentParams {
  knowledgeBaseId: string;
  documentContent: string;
  documentType: DocumentType;
  documentName: string;
  metadata?: {
    author?: string;
    createdAt?: string;
    tags?: string[];
    category?: string;
    mimeType?: string;
  };
}

export interface ProcessDocumentResponse {
  success: boolean;
  documentId: string;
  processingStatus: 'completed' | 'processing' | 'error';
  metadata?: any;
  entriesCount: number;
}

export interface QueryKnowledgeBaseParams {
    knowledgeBaseIds: string[];
    documentIds?: string[];
    query: string;
    limit?: number;
    similarityThreshold?: number;
}

export interface QueryKnowledgeBaseResponse {
    results: Array<{
        content: string;
        score: number;
        metadata: {
            documentName: string;
            documentId: string;
            chunkIndex: number;
            startOffset: number;
            endOffset: number;
        };
    }>;
}

// Define FacebookPage type matching the backend definition
export interface FacebookPage {
  id: string;
  name: string;
  accessToken?: string; // Keep optional if not always needed/returned
  permissions?: string[]; // Keep optional
  hasInstagramAccount: boolean;
  instagramAccountId?: string;
  // Add profile_picture_url if you plan to fetch/use it
  profile_picture_url?: string; 
}

// Define and export InstagramBusinessAccount type matching the backend definition
export interface InstagramBusinessAccount {
  id: string;
  name: string;
  username: string;
  profile_picture_url?: string;
  followers_count?: number;
  facebookPageId: string;
  pageAccessToken?: string; // Keep optional
  messagingEnabled?: boolean;
}

export interface GetPagesResponse {
  facebookPages: FacebookPage[]; // Use the defined type
  instagramAccounts: InstagramBusinessAccount[]; // Use the exported type
}

export interface CreditUsageData {
  planName: string;
  totalCredits: number;
  creditsUsed: number;
  creditsRemaining: number;
  percentageUsed: number;
  renewalDate: string;
  daysRemaining: number;
}

export interface UsageDataPoint {
  date: string;
  usage: number;
}

export interface BotUsageData {
  id: string;
  name: string;
  credits: number;
}

export interface CreditUsageResponse {
  creditUsageData: CreditUsageData;
  usageDataPoints: UsageDataPoint[];
  botUsageData: BotUsageData[];
}

export interface ExportCreditUsageResponse {
  url: string;
}

export interface SubscribeToPageMessagingRequest {
  pageId: string;
  pageAccessToken: string;
}

export interface SubscribeToPageMessagingResponse {
  success: boolean;
  message: string;
}

export interface UnsubscribeFromPageMessagingRequest {
  pageId: string;
  pageAccessToken: string;
}

export interface UnsubscribeFromPageMessagingResponse {
  success: boolean;
  message: string;
}

export interface SendInstagramMessageRequest {
  recipientId: string;
  pageId: string;
  text: string;
  assetUrl?: string;
}

export interface SendInstagramMessageResponse {
  success: boolean;
  messageId?: string;
  error?: string;
}

export interface RevokeTokenRequest {
  tokenType?: 'facebook' | 'instagram';
  permission?: string;
}

export interface RevokeTokenResponse {
  success: boolean;
  message: string;
}

export interface RefreshTokenRequest {
  tokenId: string;
}

export interface RefreshTokenResponse {
  success: boolean;
  message: string;
  token?: {
    tokenType: string;
    expiresAt: string;
    timeGainedHours?: number;
  };
}

export interface ValidateFacebookTokenResponse {
  isValid: boolean;
  tokenId?: string;
  message?: string;
}

export interface ValidateFacebookTokenParams {
  token: string;
}

// Dashboard data interfaces
export interface DashboardOverviewStats {
  totalAgents: number;
  deployedAgents: number;
  draftAgents: number;
  activeAgents: number;
  totalSessions: number;
  averageNodesPerAgent: number;
  botUsageGrowth: number;
}

export interface NodeTypeDistributionItem {
  name: string;
  value: number;
  color: string;
}

export interface FeatureAdoptionItem {
  name: string;
  value: number;
}

export interface KnowledgeBaseAdoptionStats {
  totalKBs: number;
  totalDocuments: number;
  agentsUsingKB: number;
  percentAgentsUsingKB: number;
}

export interface BotListItem {
  _id: string;
  name: string;
  description: string;
  owner: string;
  activeDeploymentId: string | null;
  botStats?: {
    activeSessions: number;
    todaySessions: number;
    avgDuration: string;
  };
  createdAt?: Date;
  updatedAt?: Date;
}

export interface NodeEventStats {
  totalEvents: number;
  totalErrors: number;
  eventsByType: Record<EventType, number>;
  processTypeDistribution: Record<string, number>; // Use string if ProcessType import causes issues
  totalCreditUsage: number;
}

export interface AllDashboardStats {
  overviewStats: DashboardOverviewStats;
  nodeTypeDistribution: NodeTypeDistributionItem[];
  advancedFeatureAdoption: FeatureAdoptionItem[];
  kbAdoption: KnowledgeBaseAdoptionStats;
  nodeEventStats: NodeEventStats;
}

export interface AutomationSession {
  _id: string;
  botId: string;
  botVersionId?: string;
  status: 'active' | 'completed' | 'error';
  createdAt: string;
  updatedAt: string;
  variables?: Record<string, any>;
  metadata?: Record<string, any>;
  owner?: string;
}

export interface FindSessionsByNodeEventParams {
  eventType?: EventType;
  processType?: string;
  limit: number;
  skip: number;
}

// Interface for the getAllSessions endpoint parameter
export interface GetAllSessionsParams {
  limit?: number;
  skip?: number;
  status?: string;
  sortBy?: string;
  sortOrder?: 1 | -1;
  search?: string;
}

// Bot list interface for admin-only endpoint

export const initAppData = createAsyncThunk(
  "initAppData",
  async (_, thunkApi) => {
    const { dispatch } = thunkApi;
    const settings = await dispatch(getSettings.initiate()).unwrap()
    dispatch(taskActions.setFreeLimits(settings.freeLimits))
    dispatch(setSettings(settings))
  }
);

export type TransitionData = {
  sessionId: string;
  transitions: Record<string, number>;
  lastProcessedTimestamps: Record<string, string>;
  fromEvent: string;
};

//console.log(appConfig)



export const apiBaseQuery = fetchBaseQuery({
  baseUrl: appConfig.API_ENDPOINT,
  prepareHeaders: (headers: Headers, { getState, endpoint }) => {
    const userState: UserState = (getState() as CommonRootState).UserReducer;
    const supportedUserId = new URLSearchParams(window.location.search).get('supportedUserId');
    const supportedWorkSpaceId = new URLSearchParams(window.location.search).get('workspaceId');

    if (endpoint !== 'importExcelToBoard' && endpoint !== 'createAndDeleteAutomation') {
      headers.set("Content-Type", "application/json");
    }
    if (userState?.accessToken) {
      headers.set("Authorization", `Bearer ${userState.accessToken}`);
    }
    if (userState?.socketId) {
      headers.set('Socket', userState.socketId);
    }

    if (supportedUserId) {
      headers.set('supportedUserId', supportedUserId);
    }
    if (supportedWorkSpaceId) {
      headers.set('supportedWorkSpaceId', supportedWorkSpaceId);
    }

    return headers;
  },
});
export type ApiBaseQuery = typeof apiBaseQuery;

export const tagTypes = [
  "user-messages",
  "templates",
  "lists",
  "contacts",
  "labels",
  "tasks-extra-data",
  "tasks",
  "task-groups",
  "boards",
  "message-log",
  "campaigns",
];

export type ApiTagTypes = typeof tagTypes[number];

export interface WorkspaceInvoices {
  invoiceList: BluesnapInvoice[];
  chargebeeTransactionList: ChargebeeTransaction[];
}

// Define a service using a base URL and expected endpoints
export const backendApi = createApi({
  reducerPath: "backendApi",
  tagTypes,
  keepUnusedDataFor: 1,
  baseQuery: apiBaseQuery,
  endpoints: (builder) => ({
    ...recycleApiBuilder(builder),
    ...chargebeeApiBuilder(builder),
    authenticate: builder.mutation<AuthResponse, AuthParams>({
      query: (authParams: AuthParams) => {
        return {
          url: "authentication",
          method: "POST",
          body: authParams,
        };
      },
      onQueryStarted: authenticateOnQueryStarted
    }),
    processDocument: builder.mutation<ProcessDocumentResponse, ProcessDocumentParams>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "knowledge-base.processDocument",
          ...params
        },
      }),
    }),
    register: builder.mutation<User, Partial<User>>({
      query: (registerParams: Partial<User>) => ({
        url: "users",
        method: "POST",
        body: registerParams,
      }),
      async onQueryStarted(registerParams, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          const res = await dispatch(
            authenticate.initiate({
              strategy: "local",
              email: registerParams.email,
              password: registerParams.password,
            })
          );
          authenticateOnQueryStarted(undefined, { dispatch, queryFulfilled: res });

        } catch (err) {
          console.log(err);
        }
      },
    }),
    sendResetPassword: builder.mutation<{ state: string }, string>({
      query: (email: string) => ({
        url: "authManagement",
        method: "POST",
        body: {
          action: "sendResetPwd",
          value: { email },
        },
      }),
    }),
    resetPassword: builder.mutation<any, { token: string; password: string }>({
      query: (params) => ({
        url: "authManagement",
        method: "POST",
        body: {
          action: "resetPwdLong",
          value: {
            ...params,
          },
        },
      }),
    }),

    changePassword: builder.mutation<any, { oldPassword: string; newPassword: string; }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "change-password",
          ...params,
        },
      }),
    }),
    getSettings: builder.mutation<Settings, void>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "get-settings",
        },
      }),
    }),
    generateUploadUrl: builder.mutation<GenerateUploadUrlResponse, string>({
      transformResponse: (response: any, meta, mediaId) => {
        return {
          ...response,
          mediaId,
        };
      },
      query: () => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "generate-upload-url",
        },
      }),
    }),
    cancelSubscription: builder.mutation<
      Subscription,
      { subscriptionID: string; reason: string }
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "cancelSubscription",
          subscriptionID: params.subscriptionID,
          reason: params.reason,
        },
      }),
    }),
    addUserToTaskGroup: builder.mutation<any, string>({
      query: (shareId) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "add-participant-task-group",
          shareId,
        },
      }),
    }),
    canISend: builder.mutation<{ wasSendAttempted: boolean, creationDate: string }, string>({
      query: (messageId) => ({
        url: "actions?cmd=can-i-send",
        method: "POST",
        body: {
          messageId,
        },
      }),
    }),
    addNewBoardMemberApi: builder.mutation<any, string>({
      query: (newMemberEmail) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "add-new-board-member",
          newMemberEmail,
        },
      }),
    }),
    nextCampaignMessage: builder.mutation<NextCampaignMessageResponse, string>({
      query: (campaignId: string) => {
        // console.log('call for next-campaign-message cmd')
        return {
          url: "actions?cmd=next-campaign-message",
          method: "POST",
          body: { campaignId },
        };
      },
    }),
    getInProgressCampaigns: builder.mutation<Campaign[], void>({
      query: () => {
        return {
          url: "actions?cmd=get-in-progress-campaigns",
          method: "POST",
        };
      },
    }),

    getScheduledMessagesInRedis: builder.query<any, string>({
      query: (campaignId) => ({
        url: "actions?cmd=get-scheduled-messages-in-redis",
        method: "POST",
        body: { 
          campaignId 
        },
      }),
    }),

    btWaCommRequest: builder.mutation<Array<{_id: string, imgUrl?: string, profilePicLastUpdate: string}>, any>({
      query: ({ contactIds, type }) => {
        return {
          url: "actions?cmd=bt-wa-communication-rest-api-request",
          method: "POST",
          body: { contactIds, type },
        };
      },
      onQueryStarted: waCommRequestOnQueryStarted
    }),
    duplicateAudienceBoardForCampaigns: builder.mutation<duplicateAudienceBoardForCampaignsResponse, string>({
      query: (oldBoardId: string) => {
        return {
          url: "actions",
          method: "POST",
          body: { cmd: "duplicate-audience-board-for-campaigns", oldBoardId },
        };
      },
    }),
    duplicateTemplateForCampaigns: builder.mutation<duplicateTemplateForCampaignsResponse, string>({
      query: (oldTemplateId: string) => {
        return {
          url: "actions",
          method: "POST",
          body: { cmd: "duplicate-template-for-campaigns", oldTemplateId },
        };
      },
    }),
    removeAllPendingUserMessagesByCampaignId: builder.mutation<any, string>({
      query: (campaignId: string) => {
        return {
          url: "actions",
          method: "POST",
          body: { cmd: "remove-all-pending-user-messages-by-campaignId", campaignId },
        };
      },
    }),
    resumeCampaign: builder.mutation<NextCampaignMessageResponse, string>({
      query: (campaignId: string) => {
        return {
          url: "actions",
          method: "POST",
          body: { cmd: "resume-campaign", campaignId },
        };
      },
    }),
    deleteCampaign: builder.mutation<Campaign, string>({
      query: (campaignId: string) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "delete-campaign", campaignId },
      }),
    }),
    orderDetails: builder.mutation<Invoice[], string>({
      query: (subscriptionId: string) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "order-details", subscriptionId },
      }),
    }),
    getUserPaymentParams: builder.mutation<
      UserPaymentParams,
      GetUserPaymentParamsRequest
    >({
      query: ({ productId }) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "user-payment-params", productId },
      }),
    }),
    getPlanMap: builder.mutation<Record<string, PlanDescriptor>, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "plan-map" },
      }),
    }),
    changePlan: builder.mutation<
      { changeSuccessful: boolean },
      { contractId: string }
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "change-plan", ...params },
      }),
    }),

    completePurchase: builder.mutation<
      { changeSuccessful: boolean, message?: string },
      ShoppingContextParams
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "complete-purchase", ...params },
      }),
      transformResponse: (response, meta) => {
        const hasError = !!(response as any).error || !!(response as any).message;
        const [errorMessage] = (response as any).message ?? [];
        return { changeSuccessful: !hasError, message: errorMessage?.description };
      }
    }),
    getPaymentFieldToken: builder.mutation<
      { token: string; endpoint: string },
      void
    >({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "payment-fields-token" },
      }),
    }),
    calculateCart: builder.mutation<BluesnapCartInfo & BluesnapErrorOutput & CalculateCartParams, CalculateCartParams>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "get-cart-price", ...params },
      }),
      transformResponse: (response: any, meta, params) => {
        return {
          ...response,
          ...params
        };
      }
    }),
    getWorkspaceInvoices: builder.mutation<WorkspaceInvoices, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "get-workspace-invoices" },
      }),
    }),
    getInvoice: builder.mutation<BluesnapInvoice, { invoiceId: string }>({
      query: ({ invoiceId }) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "get-invoice", invoiceId },
      }),
    }),
    getShopperInfo: builder.mutation<{
      planName: string,
      shopper: BluesnapShopper;
      customer: Customer;
      subscription: Subscription;
      paymentSourceList: PaymentSource[];
      manage_payment_sources_url?: string;
    }, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "get-shopper-info" },
      }),
      transformResponse: (response: any) => {
        return response?.error ? undefined : response;
      },
    }),
    getPaypalPaymentUrl: builder.mutation<BluesnapShoppingContext & BluesnapErrorOutput, ShoppingContextParams>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "get-paypal-payment-url", ...params },
      }),
      transformResponse: (response: any, meta, params) => {
        return {
          ...response,
          ...params
        };
      }
    }),
    getCheckoutPageUrl: builder.mutation<GetCheckoutPageUrlResponse, GetCheckoutPageUrlParams>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "chargebee.getCheckoutPage", ...params },
      })
    }),
    cancelActiveSubscription: builder.mutation<void, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "chargebee.cancelActiveSubscription" },
      }),
    }),
    getPortalSessionUrl: builder.mutation<{
      portal_session_url: string;
    }, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "chargebee.getPortalSessionUrl" },
      }),
    }),
    updateShopperInfo: builder.mutation<void, { shopperInfo: ShopperInfo }>({
      query: ({ shopperInfo }) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "update-shopper-info", shopperInfo },
      }),
    }),
    getEmailWorkspaces: builder.mutation<void, { email: string }>({
      query: ({ email }) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "get-user-workspaces", email },
      }),
    }),
    updateSubscriptionWorkspace: builder.mutation<void, { workspaceId: string, subscriptionId: string }>({
      query: ({ workspaceId, subscriptionId }) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "update-subscription-workspace", workspaceId, subscriptionId },
      }),
    }),

    getSignedUploadUrl: builder.mutation<
      { url: string; publicUrl: string },
      string
    >({
      query: (contentType: string) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "signed-upload-url", contentType },
      }),
    }),
    getEntity: builder.query<any, GetParams>({
      query: ({ service, _id }) => ({
        url: `${service}/${_id}`,
        method: "GET",
      }),
    }),
    findEntity: builder.query<Paginated<any>, FindParams>({
      query: ({ service, query }) => {
        let url = service;
        if (query) {
          url += `/?${qs.stringify(query)}`;
        }
        return {
          url,
          method: "GET",
        };
      },
    }),

    // AWS SES EMAIL TESTING
    // send awsEmail
    sendAwsEmail: builder.mutation<void, { email: string, templateId: string, title:string }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "send-aws-email-to-email", ...params },
      }),
    }),

    // send aws email to subscriber
    sendAwsEmailToSubscriber: builder.mutation<void, { email: string, templateId: string, title:string }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "send-aws-email-to-subscriber", ...params },
      }),
    }),
    sendEmailFromClient: builder.mutation<void, { userDeletedTitle: string, userDeletedEmail: string }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "send-email-from-client", ...params },
      }),
    }),


    // unsubscribe from listmonk
    unsubscribeFromListmonk: builder.mutation<void, { subscriptionID: string }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "unsubscribe-from-listmonk", ...params },
      }),
    }),

    getTotalCount: builder.query<number, { service: string }>({
      query: ({ service }) => {
        let url = service;
        const query = { $limit: 0 }; // Only fetch the total count
        url += `/?${qs.stringify(query)}`;
        return {
          url,
          method: "GET",
        };
      },
      transformResponse: (response: { total: number }) => response.total,
    }),
    createEntity: builder.mutation<any, CreateParams>({
      query: ({ service, entity }) => ({
        url: `${service}`,
        method: "POST",
        body: entity,
      }),
    }),

    patchEntity: builder.mutation<any, CreateParams>({
      query: ({ service, entity }) => ({
        url: `${service}/${entity._id}`,
        method: "PATCH",
        body: entity,
      }),
    }),
    deleteEntity: builder.mutation<any, CreateParams>({
      query: ({ service, entity }) => ({
        url: `${service}/${entity._id}`,
        method: "DELETE",
        body: entity,
      }),
    }),
    updateManyEntities: builder.mutation<
      UpdateManyResponse<any>,
      CreateManyParams
    >({
      query: ({ service, entities }) => ({
        url: "actions?cmd=update-many-entities",
        method: "POST",
        body: {
          cmd: "update-many-entities",
          service,
          entities,
        },
      }),
    }),
    createManyEntities: builder.mutation<
      CreateManyResponse<any>,
      CreateManyParams
    >({
      query: ({ service, entities }) => ({
        url: "actions?cmd=create-many-entities",
        method: "POST",
        body: {
          cmd: "create-many-entities",
          service,
          entities,
        },
      }),
    }),



    addExistingWorkspaceUserToBoard: builder.mutation<
      any,
      {
        boardId: string;
        userId: string;
        role: string;
      }
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "add-existing-workspace-user-to-board",
          ...params
        },
      }),
    }),
    addNonExistingWorkspaceUserToBoard: builder.mutation<
      any,
      {
        boardId: string;
        email: string;
        role: string;
      }
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "add-non-existing-workspace-user-to-board",
          boardId: params.boardId,
          email: params.email,
          role: params.role,
        },
      }),
    }),

    resendBoardInvitation: builder.mutation<
      any,
      {
        boardId: string;
        boardUserId: string;
        email: string;
      }
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "resend-board-invitation",
          boardId: params.boardId,
          boardUserId: params.boardUserId,
          email: params.email,

        },
      }),
    }),
    //   Send a Massage code
    sendAddToWorkspace: builder.mutation<void, string>({
      query: (email: string) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "send-add-to-workspace",
          value: { email },
        },
      }),
    }),
    importExcelToBoard: builder.mutation<void, ImportExcelToBoardOptions>({
      query: (options: ImportExcelToBoardOptions) => {
        const formData = new FormData();

        formData.append('cmd', 'import-excel')
        formData.append('boardId', options.boardId)
        formData.append('selectedColumn', options.selectedColumn)
        formData.append('importDataMapping', JSON.stringify(options.importDataMapping))
        formData.append('sheetName', JSON.stringify(options.sheetName))
        formData.append('importFileName', JSON.stringify(options.importFileName))
        formData.append('file', options.file)

        return {
          url: "actions",
          method: "POST",
          body: formData,
        }
      },
    }),
    initialStep: builder.mutation<TransitionData, { cmd: string; event: string }>({
      query: (body) => ({
        url: '/analytics',
        method: 'POST',
        body,
      }),
    }),
    getNextStep: builder.mutation<TransitionData, { cmd: string; sessionId: string; event: string }>({
      query: (body) => ({
        url: '/analytics',
        method: 'POST',
        body,
      }),
    }),
    acceptWorkspaceInvention: builder.mutation<
      AcceptInventionResponse,
      AcceptWorkspaceInvitationParams
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "accept-workspace-invention",
          ...params
        },
      }),
    }),
    acceptBoardInvention: builder.mutation<
      AcceptInventionResponse,
      AcceptBoardInvitationParams
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "accept-board-invention",
          boardId: params.boardId,
          ...params
        },
      }),
    }),
    getLinkPreview: builder.mutation<LinkPreview, { link: string }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "link-preview",
          ...params,
        },
      }),
    }),
    createAndDeleteAutomation: builder.mutation<void, any>({
      query: (options: any) => {
        ;
        const formData = new FormData();
        formData.append('cmd', 'automation')
        formData.append('automationDetails', JSON.stringify(options.combined))
        formData.append('automationId', JSON.stringify(options.automationId))
        return {
          url: "actions",
          method: "POST",
          body: formData,
        }
      },
    }),
    checkSession: builder.mutation<Settings, void>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "check-session",
        },
      }),
    }),
    createSession: builder.mutation<Settings, void>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "create-session",
        },
      }),
    }),
    duplicateGroup: builder.mutation<any, { groupId: string }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "duplicate-group",
          ...params,
        },
      }),
    }),

    generateApiKey: builder.mutation<
      Partial<User>,
      Partial<User>
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "generate-api-key",
          ...params
        },
      }),
    }),
    getArchivedOrDeletedData: builder.mutation<any, { mode: string, itemId, itemUpdatedAt, searchQuery: string, type?: string[], date?: string, board?: string[] }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "get-archived-or-deleted-data",
          ...params,
        },
      }),
    }),
    restoreItems: builder.mutation<any, { items: ArchivedOrDeletedItem[], deleteOrArchive: string }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "restore-items",
          ...params,
        },
      }),
    }),
    deleteItems: builder.mutation<any, { items: ArchivedOrDeletedItem[] }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "delete-items",
          ...params,
        },
      }),
    }),
    getMyWork: builder.mutation<any, {}>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "get-my-work",
          ...params,
        },
      }),
    }),
    confirmEmail: builder.mutation<any, {}>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "send-email-confirmation",
          ...params,
        },
      }),
    }),
    startOrExtendTrial: builder.mutation<any, {}>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "start-or-extend-trial",
          ...params,
        },
      }),
    }),
    isWaSocketsLimitReached: builder.mutation<any, {}>({
      query: () => ({
        url: "actions?cmd=is-wa-sockets-limit-reached",
        method: "POST",
      }),
    }),
    triggerBulkMigrationAction: builder.mutation<any, {}>({
      query: () => ({
        url: "actions?cmd=trigger-bulk-migration",
        method: "POST",
      }),
    }),
    triggerBackendChatbotEvent: builder.mutation<any, { data: any }>({
      query: (params) => ({
        url: "actions?cmd=handle-chatbot-event",
        method: "POST",
        body: {
          ...params,
          data: {
            ...params.data,
            triggerSource: 'blueticks extension'
          }
        }
      }),
    }),
    updateChargeDate: builder.mutation<
      Subscription,
      { subscriptionId: string; nextChargeDate: string }
    >({
      query: (params) => ({
        url: "backoffice-actions",
        method: "POST",
        body: {
          cmd: "updateChargeDate",
          subscriptionId: params.subscriptionId,
          nextChargeDate: params.nextChargeDate,
        },
      }),
    }),
    getItemPrices: builder.mutation<GetItemPricesResponse, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "chargebee.getItemPrices" },
      })
    }),
    getWaMessagesAckDataByWaMessagesKeys: builder.mutation<any, { messageKeys: Array<{ key: string, key2: string }> }>({
      query: (params) => ({
        url: "/actions",
        method: "POST",
        body: {
          cmd: "whatsapp-interface.getWaMessagesAckDataByWaMessagesKeys",
          params,
        },
      }),
    }),
    getEntitlements: builder.mutation<Record<string, Entitlement[]>, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "chargebee.getEntitlements" },
      }),
    }),
    associateSubscription: builder.mutation<void, { customerId: string }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "chargebee.associateSubscription", ...params },
      }),
    }),
    queryKnowledgeBase: builder.mutation<QueryKnowledgeBaseResponse, QueryKnowledgeBaseParams>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "knowledge-base.query",
          ...params
        },
      }),
    }),
    getFacebookPages: builder.mutation<GetPagesResponse, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "facebook.getPages",
        },
      }),
    }),
    getCreditUsage: builder.mutation<CreditUsageResponse, { timeframe: string }>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "credits.getCreditUsage",
          ...params
        },
      }),
    }),
    calculateBotStats: builder.mutation<any, string[]>({
      query: (botIds) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "calculate-bot-stats",
          botIds
        },
      }),
      async onQueryStarted(botIds, { dispatch, queryFulfilled }) {
        try {
          const { data: botStats } = await queryFulfilled;
          
          if (botStats && Array.isArray(botStats)) {
            botStats.forEach(botWithStats => {
              if (botWithStats._id && botWithStats.botStats) {
                dispatch({
                  type: `bot/updateOne`,
                  payload: {
                    id: botWithStats._id,
                    changes: { botStats: botWithStats.botStats }
                  }
                });
              }
            });
          }
        } catch (error) {
          console.error('Error updating bot stats:', error);
        }
      }
    }),
    queryAndAnswer: builder.mutation<
      { generatedAnswer: string; rerankedResponse: any },
      { knowledgeBaseIds: string[]; documentIds?: string[]; query: string; limit?: number; similarityThreshold?: number }
    >({
      query: (body) => ({
        url: '/actions',
        method: 'POST',
        body: {
          cmd: 'knowledge-base.queryAndAnswer',
          ...body,
        },
      }),
    }),
    subscribeToPageMessaging: builder.mutation<SubscribeToPageMessagingResponse, SubscribeToPageMessagingRequest>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "instagram.subscribeToPageMessaging", ...params },
      }),
    }),
    unsubscribeFromPageMessaging: builder.mutation<UnsubscribeFromPageMessagingResponse, UnsubscribeFromPageMessagingRequest>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "instagram.unsubscribeFromPageMessaging", ...params },
      }),
    }),
    // Get auth tokens for the current user
    getAuthTokens: builder.query<any[], void>({
      query: () => ({
        url: "auth-tokens",
        method: "GET"
      }),
      transformResponse: (response: any) => {
        // Handle paginated response
        if (response && response.data) {
          return response.data;
        }
        return response;
      }
    }),
    
    // Delete an auth token
    deleteAuthToken: builder.mutation<void, string>({
      query: (tokenId) => ({
        url: `auth-tokens/${tokenId}`,
        method: "DELETE"
      }),
      // Invalidate the auth tokens query when a token is deleted
      invalidatesTags: [{ type: 'AuthTokens', id: 'LIST' }]
    }),
    revokeToken: builder.mutation<RevokeTokenResponse, RevokeTokenRequest>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "instagram.revokeToken", ...params },
      }),
      invalidatesTags: ["Instagram"],
    }),
    refreshToken: builder.mutation<RefreshTokenResponse, RefreshTokenRequest>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { cmd: "instagram.refreshToken", ...params },
      }),
      invalidatesTags: [{ type: 'AuthTokens', id: 'LIST' }],
    }),
    // Validate Facebook token
    validateFacebookToken: builder.mutation<ValidateFacebookTokenResponse, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "facebook.validateToken" },
      }),
    }),
    // Dashboard statistics endpoints
    getDashboardOverviewStats: builder.mutation<DashboardOverviewStats, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "dashboard.getOverviewStats" },
      }),
    }),
    
    getNodeTypeDistribution: builder.mutation<NodeTypeDistributionItem[], void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "dashboard.getNodeTypeDistribution" },
      }),
    }),
    
    getAdvancedFeatureAdoption: builder.mutation<FeatureAdoptionItem[], void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "dashboard.getAdvancedFeatureAdoption" },
      }),
    }),
    
    getKnowledgeBaseAdoption: builder.mutation<KnowledgeBaseAdoptionStats, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "dashboard.getKnowledgeBaseAdoption" },
      }),
    }),
    
    getAllDashboardStats: builder.mutation<AllDashboardStats, void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "dashboard.getAllStats" },
      }),
    }),
    
    getBotList: builder.mutation<BotListItem[], void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "dashboard.getBotList" },
      }),
    }),
    sendInstagramMessage: builder.mutation<SendInstagramMessageResponse, SendInstagramMessageRequest>({
      query: (params) => ({
        url: 'actions',
        method: 'POST',
        body: {
          cmd: 'instagram.sendMessage',
          data: params
        }
      }),
      invalidatesTags: []
    }),
    findSessionsByNodeEvent: builder.query<
      Paginated<AutomationSession>,
      { eventType?: EventType; processType?: string; limit?: number; skip?: number }
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { 
          cmd: "session.findSessionsByNodeEvent",
          ...params
        },
      }),
    }),
    getAllSessions: builder.query<
      Paginated<any>,
      GetAllSessionsParams
    >({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: { 
          cmd: "dashboard.getAllSessions",
          ...params
        },
      }),
    }),
    getConnectedClients: builder.mutation<ConnectedSocket[], void>({
      query: () => ({
        url: "actions",
        method: "POST",
        body: { cmd: "emqx.getConnectedClients" },
      }),
    }),
    refundClient: builder.mutation<any, {}>({
      query: (params) => ({
        url: "backoffice-actions",
        method: "POST",
        body: {
          cmd: "refund-client",
          ...params,
        },
      }),
    }),
    syncUserData: builder.mutation<any, {}>({
      query: (params) => ({
        url: "actions",
        method: "POST",
        body: {
          cmd: "sync-user-data",
          ...params,
        },
      }),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetSignedUploadUrlMutation,
  useAuthenticateMutation,
  useRegisterMutation,
  useGenerateUploadUrlMutation,
  useGetEntityQuery,
  usePatchEntityMutation,
  useSendResetPasswordMutation,
  useResetPasswordMutation,
  useChangePasswordMutation,
  useCancelSubscriptionMutation,
  useAddUserToTaskGroupMutation,
  useAddNewBoardMemberApiMutation,
  useFindEntityQuery,
  useGetTotalCountQuery,
  useUnsubscribeFromListmonkMutation,
  useCreateEntityMutation,
  useAddExistingWorkspaceUserToBoardMutation,
  useAddNonExistingWorkspaceUserToBoardMutation,
  useResendBoardInvitationMutation,
  useSendAddToWorkspaceMutation,
  useAcceptWorkspaceInventionMutation,
  useAcceptBoardInventionMutation,
  useGetLinkPreviewMutation,
  useDuplicateGroupMutation,
  useOrderDetailsMutation,
  useGetUserPaymentParamsMutation,
  useGetPlanMapMutation,
  useGetPaymentFieldTokenMutation,
  useChangePlanMutation,
  useCompletePurchaseMutation,
  useCalculateCartMutation,
  useGetWorkspaceInvoicesMutation,
  useGetInvoiceMutation,
  useGetShopperInfoMutation,
  useGetPaypalPaymentUrlMutation,
  useUpdateShopperInfoMutation,
  useImportExcelToBoardMutation,
  useInitialStepMutation,
  useGetNextStepMutation,
  useGetMyWorkMutation,
  useConfirmEmailMutation,
  useStartOrExtendTrialMutation,
  useIsWaSocketsLimitReachedMutation,
  useGenerateApiKeyMutation,
  useCreateAndDeleteAutomationMutation,
  useTriggerBulkMigrationActionMutation,
  useGetEmailWorkspacesMutation,
  useUpdateSubscriptionWorkspaceMutation,
  useGetCheckoutPageUrlMutation,
  useGetItemPricesMutation,
  useGetScheduledMessagesInRedisQuery,
  useGetEntitlementsMutation,
  useProcessDocumentMutation,
  useQueryKnowledgeBaseMutation,
  useQueryAndAnswerMutation,
  useGetFacebookPagesMutation,
  useGetWaMessagesAckDataByWaMessagesKeysMutation,
  useGetCreditUsageMutation,
  useCalculateBotStatsMutation,
  useSendEmailFromClientMutation,
  useSubscribeToPageMessagingMutation,
  useUnsubscribeFromPageMessagingMutation,
  useGetAuthTokensQuery,
  useDeleteAuthTokenMutation,
  useRevokeTokenMutation,
  useRefreshTokenMutation,
  useValidateFacebookTokenMutation,
  useGetDashboardOverviewStatsMutation,
  useGetNodeTypeDistributionMutation,
  useGetAdvancedFeatureAdoptionMutation,
  useGetKnowledgeBaseAdoptionMutation,
  useGetAllDashboardStatsMutation,
  useGetBotListMutation,  
  useSendInstagramMessageMutation,
  useFindSessionsByNodeEventQuery,
  useGetAllSessionsQuery,
  useGetConnectedClientsMutation
} = backendApi;
export const backendApiUseMutations = backendApi;

export const {
  addExistingWorkspaceUserToBoard,
  addNonExistingWorkspaceUserToBoard,
  resendBoardInvitation,
  authenticate,
  getEntity,
  findEntity,
  getTotalCount,
  unsubscribeFromListmonk,
  createEntity,
  createManyEntities,
  patchEntity,
  deleteEntity,
  deleteCampaign,
  resumeCampaign,
  nextCampaignMessage,
  getInProgressCampaigns,
  getScheduledMessagesInRedis,
  duplicateAudienceBoardForCampaigns,
  duplicateTemplateForCampaigns,
  removeAllPendingUserMessagesByCampaignId,
  updateManyEntities,
  changePlan,
  cancelSubscription,
  updateChargeDate,
  getSettings,
  createAndDeleteAutomation,
  checkSession,
  createSession,
  getArchivedOrDeletedData,
  restoreItems,
  deleteItems,
  getMyWork,
  isWaSocketsLimitReached,
  triggerBulkMigrationAction,
  btWaCommRequest,
  syncUserData,
  refundClient,
  canISend: wasSendAttempted,
  updateShopperInfo,
  getShopperInfo,
  getCheckoutPageUrl,
  triggerBackendChatbotEvent,
  processDocument,
  queryKnowledgeBase,
  queryAndAnswer,
  getFacebookPages,
  getWaMessagesAckDataByWaMessagesKeys,
  getCreditUsage,
  calculateBotStats,
  sendEmailFromClient,
  subscribeToPageMessaging,
  unsubscribeFromPageMessaging,
  revokeToken,
  refreshToken,
  validateFacebookToken,
  getDashboardOverviewStats,
  getNodeTypeDistribution,
  getAdvancedFeatureAdoption,
  getKnowledgeBaseAdoption,
  getAllDashboardStats,
  getBotList,
  sendInstagramMessage,
  findSessionsByNodeEvent,
  getAllSessions,
  getConnectedClients
} = backendApi.endpoints;
export const backendApiEndpoints = backendApi.endpoints;
export const middleware = backendApi.middleware;
export default backendApi.reducer;
